<!-- CmmEPATestCaseGallery -->
<script lang="ts" setup>
    import { ChoiceApi } from '../../../LibMain';
    import ActionButtonV2 from '../../../components/ActionButtonV2.vue';
    import { ActionButtonStyle } from '../../../lib/Types/Types';
    import { UserSettingGroup, Alignment  } from '../../../Enums/ChoiceEnums';
    import { ButtonDisplaySize, ButtonDisplayStyle, DisplayStyle } from '../../../Enums/UiEnums';
    import IntegerInput from '../../../components/IntegerInput.vue';
    import { reactive, onMounted } from 'vue';
    import ChoiceButton from '../../../components/Buttons/ChoiceButton.vue';
    import { popAlert } from '../../../lib/Notification';
    import MessageSection from '../../../components/Messages/MessageSection.vue';

    async function sendCase(testCase: string|undefined)
    {
        // Do we want to disable testing?
        if (disableTestCaseValue === testCase)
        {
            await ChoiceApi.user.saveSetting(UserSettingGroup.TestSettings, 'CMMTC', '');
            popAlert("CMM Test cases disabled");
            return;
        }

        if (!compData.prescriptionId)
        {
            popAlert("compData.prescriptionId is invalid");
            return;
        }
        await ChoiceApi.user.saveSetting(UserSettingGroup.TestSettings, 'CMMTC', testCase);
        compData.currentTestCase = testCase;

        const resp = await ChoiceApi.coverMyMeds.initiateEpaRequest(compData.prescriptionId);
        if (!resp)
        {
            await popAlert("Failed making call");
            return;
        }
        if (!resp.passed)
        {
            await popAlert(`Error calling CMM: ${resp.message ?? "n/a"}`);
        }
    }

    const compData = reactive<{
        prescriptionId?: number,
        currentTestCase?: string
    }>({
        prescriptionId: 7172
    });

    onMounted(async () =>{
        compData.currentTestCase = await ChoiceApi.user.getSetting(UserSettingGroup.TestSettings, 'CMMTC');
    });

    const disableTestCaseValue = 'Disable'

    const testCases = [
        {case: '1', name: 'Happy Path'},
        {case: '2', name: 'Sad Path'},
        {case: '3', name: 'Question Loop'},
        {case: '4', name: 'Closed Response'},
        {case: '5', name: 'Cancel a Request'},
        {case: '6.1', name: 'File Attachments - Outbound'},
        {case: '6.2', name: 'File Attachments - Incoming'},
        {case: '7', name: 'PA Not Needed'},
        {case: '8', name: 'Classic PA', notes: 'IDC10 code: E06.0 or E78.5', disabled: true},
        {newCaseId: '8', case: '9', name: 'Insurance Search', notes: 'Plan name: Mock Pseudo, RxBIN: 773836, RxPCN: MOCKPBM, RxGroup ID: ABC1'},
        {newCaseId: '9', case: '10', name: 'Insurance Search – Plan/PBM Name Only', notes: 'RxBIN: 773836'}, 
        {newCaseId: '10', case: '11', name: 'Numeric Next-Question Conditions'},
        {newCaseId: '11.1', case: '12.1', name: 'Drug Specific Question Set: Belbuca'},
        {newCaseId: '11.2', case: '12.2', name: 'Drug Specific Question Set: Ravicti'},
        {case: disableTestCaseValue, name: "Normal Operation"},
    ];

    function buttonDisplayStyle(testCase: any): DisplayStyle | ButtonDisplayStyle
    {
        if (testCase.disabled)
            return ButtonDisplayStyle.OutlineDanger;

        return compData.currentTestCase === testCase.case ? DisplayStyle.Success : ButtonDisplayStyle.OutlinePrimary;
    }

    function testButtonText(testCase: any)
    {
        if (disableTestCaseValue == testCase.case)
            return "Disable";

        if (undefined === testCase.newCaseId)
            return testCase.case;

        return `${testCase.newCaseId} (Was ${testCase.case})`;
    }
</script>

<template>
    <div class="mb-1">
        Prescription ID
        <IntegerInput v-model:numberValue="compData.prescriptionId" :alignment="Alignment.left" />
        <ChoiceButton :action="ActionButtonStyle.Edit" 
                        text="7172 (most_dev)" 
                        @click="() => compData.prescriptionId = 7172" 
                        :displayStyle="DisplayStyle.Info"
                        :size="ButtonDisplaySize.Small" />
        <ChoiceButton :action="ActionButtonStyle.Edit" 
                        text="??? (CoreMC)" 
                        @click="() => compData.prescriptionId = undefined" 
                        :displayStyle="DisplayStyle.Info"
                        :size="ButtonDisplaySize.Small"/>

    </div>

    <MessageSection v-if="compData.currentTestCase?.length" :displayStyle="DisplayStyle.Danger">
        Current Test Case: {{ compData.currentTestCase }}
    </MessageSection>

    <table class="table table-striped" v-if="(compData.prescriptionId ?? 0) > 0">
        <caption style="display:none">Tests</caption>
        <thead>
            <tr>
                <th></th>
                <th>Case</th>
                <th>Description</th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="testCase in testCases" class="mb-1">
                <td style="width:2em">
                    <ActionButtonV2 
                        :disabled="testCase.disabled ?? false"
                        :action="ActionButtonStyle.Send" 
                        :performAction="async () => await sendCase(testCase.case)" 
                        :size="ButtonDisplaySize.Small"
                        :displayStyle="buttonDisplayStyle(testCase)"
                        text=""
                        spinText=""
                        tooltip="Start test case" />                    
                </td>
                <td>
                    {{ testButtonText(testCase) }}
                </td>
                <td>
                    {{ testCase.name }}
                    <div v-if="testCase.notes">
                        <em class="ms-2 text-muted">
                            {{ testCase.notes }}
                        </em>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>